import { Box, Typography } from '@mui/material';
import { splitLabelled } from '../../content';
import { tokens } from '../../theme';
import type { MasteryLevelsProps } from './types';

/**
 * The progression ladder, one numbered row per level, with the method's target
 * level picked out.
 *
 * **Each entry is split on its first colon**, which is a real convention in the
 * content rather than a guess: `Practitioner: Leads field inquiries and
 * coordinates synthesis safely.` The colon is not drawn. An entry without one
 * becomes a label with no description, so a ladder that has not adopted the
 * convention still reads as a list of levels.
 *
 * **The target row is marked three ways, not one.** It gets the tinted ground,
 * the filled number and the bolder label — and its name gains "(Target)", so
 * the distinction survives being read aloud or printed in greyscale. Colour
 * alone would be the whole signal (WCAG 1.4.1).
 *
 * **The tint bleeds past the ladder's edges rather than indenting the row** — a
 * negative horizontal margin cancels the panel's padding, so all five numbers
 * share one left edge. The Figma frames draw the target indented; this departs
 * from them deliberately (LI-7738).
 *
 * The target's description darkens to `text.primary`: it is the row someone is
 * actually being asked to read.
 *
 * **The disc and label take `accent.info`, not `text.link`.** The row is a
 * highlight, not a link, and `text.link` is 4.30:1 on `accent.blueSurface` —
 * below AA. `accent.info` was built for exactly this (a coloured label on a
 * tinted ground) and clears it at 4.59:1.
 */
export const MasteryLevels = ({ levels, target }: MasteryLevelsProps) => {
  if (levels.length === 0) {
    return null;
  }

  return (
    <Box
      component="ol"
      sx={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {levels.map((entry, index) => {
        const { label, description } = splitLabelled(entry);
        const isTarget = target !== undefined && label.toLowerCase() === target.toLowerCase();

        return (
          <Box
            key={entry}
            component="li"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              ...(isTarget
                ? {
                    backgroundColor: 'accent.blueSurface',
                    borderRadius: `${String(tokens.radius.md)}px`,
                    padding: 2,
                    marginLeft: -2,
                    marginRight: -2,
                  }
                : {}),
            }}
          >
            <Box
              aria-hidden
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 24,
                height: 24,
                borderRadius: `${String(tokens.radius.full)}px`,
                backgroundColor: isTarget ? 'accent.info' : 'border.default',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: isTarget ? 'text.onBrand' : 'text.primary',
                }}
              >
                {index + 1}
              </Typography>
            </Box>

            <Box>
              <Typography
                component="p"
                sx={{
                  fontFamily: tokens.fontFamily.heading,
                  fontSize: 13,
                  fontWeight: isTarget ? 700 : 600,
                  lineHeight: 1.4,
                  color: isTarget ? 'accent.info' : 'text.primary',
                }}
              >
                {label}
                {isTarget && ' (Target)'}
              </Typography>

              {description !== undefined && (
                <Typography
                  component="p"
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.5,
                    color: isTarget ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
